/**
 * AI Observability — Overview.
 *
 * Answers "how did the AI do", not "how fast and how costly": what broke, where
 * calls went off (setup, then input → context → reasoning → answer → checks),
 * and what needs attention, each linked to the trace that shows why. Cost and
 * speed are the last card on purpose.
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { aiObservabilityService } from '../../services/ai-observability.service';
import {
  KIND_COLOR, OBS_TYPE_INFO, fmtCost, fmtMs, kindLabel, stepLabel,
  type ObsAttention, type ObsStage, type ObsOverview, type ObsTraceRow,
} from '../../types/ai-observability.types';
import { CHIP_FAIL, ICONS, fmtWhen, useObsQuery } from './obs-kit';
import { LAYER_INFO } from './obs-layers';
import { CardHead, ErrorCard, Icon, ObsHeader, ObsState } from './obs-shell';

import '../error-analytics/error-analytics.css';
import './ai-observability.css';

const BUCKET_INFO: Record<ObsStage, { title: string; what: string }> = {
  setup: { title: 'Setup', what: 'Model, keys, connection, index — not the AI itself.' },
  ...LAYER_INFO,
};

const WARN = { background: '#FEF3C7', borderColor: '#FDE68A', color: '#92400E' } as const;
const OK = { background: '#F0FDF4', borderColor: '#BBF7D0', color: '#166534' } as const;
const INK = { fail: '#DC2626', warn: '#D97706', ok: '#16A34A', none: 'var(--obs-ink-4)' } as const;

const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)}%`);

/** The path a call takes, with how many calls went wrong at each stop. */
function WhereItWentOff({ data }: { data: ObsOverview }) {
  const worst = data.byLayer.reduce((m, b) => Math.max(m, b.fail + b.warn), 0);
  return (
    <section className="ea-card pad ea-c12" style={{ minWidth: 0 }}>
      <CardHead icon={ICONS.layers} title="Where it went off"
        sub="Every call passes through these in order. Each number is how many calls went wrong at that point — a failure stops the call, a warning means it answered anyway." />
      <div className="obs-path">
        {data.byLayer.map((b, i) => {
          const total = b.fail + b.warn;
          const tone = b.fail ? 'fail' : b.warn ? 'warn' : 'ok';
          return (
            <div key={b.layer} className={`obs-stop ${tone}${total && total === worst ? ' worst' : ''}`}>
              <div className="obs-stop-top">
                <span className="obs-stop-n">{i === 0 ? <Icon d={ICONS.gear} size={11} /> : i}</span>
                <span className="obs-stop-title">{BUCKET_INFO[b.layer].title}</span>
              </div>
              <div className="obs-stop-count" style={{ color: INK[tone] }}>{total || '✓'}</div>
              <div className="obs-stop-detail">
                {total
                  ? [b.fail && `${b.fail} failed`, b.warn && `${b.warn} went off`].filter(Boolean).join(' · ')
                  : 'no problems'}
              </div>
              <div className="obs-stop-what">{BUCKET_INFO[b.layer].what}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AttentionRow({ a }: { a: ObsAttention }) {
  const fail = a.level === 'fail';
  return (
    <Link to={`/ai-observability/tracing/${a.latestId}`} className="obs-attn">
      <span className="obs-attn-dot" style={{ background: fail ? INK.fail : INK.warn }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>{a.name}</span>
          <span className="obs-chip" style={{ color: KIND_COLOR[a.kind], fontWeight: 600 }}>{kindLabel(a.kind)}</span>
          <span className="obs-chip" style={fail ? CHIP_FAIL : WARN}>
            {BUCKET_INFO[a.layer].title} · {stepLabel(a.problem)}
          </span>
        </div>
        <div className="ea-sub obs-attn-msg" title={a.message}>{a.message}</div>
      </div>
      <div style={{ textAlign: 'right', flex: 'none' }}>
        <div style={{ fontWeight: 600, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>×{a.count}</div>
        <div className="ea-sub" style={{ fontSize: 11.5 }}>{fmtWhen(a.lastAt, 'minute')}</div>
      </div>
    </Link>
  );
}

function RecentRow({ r }: { r: ObsTraceRow }) {
  const tone = r.status === 'FAILED' ? 'fail' : r.warningCount ? 'warn' : 'ok';
  return (
    <tr>
      <td className="obs-mono" style={{ whiteSpace: 'nowrap' }}>{fmtWhen(r.startedAt, 'minute')}</td>
      <td>
        <span className="obs-chip" style={tone === 'fail' ? CHIP_FAIL : tone === 'warn' ? WARN : OK}>
          {tone === 'fail' ? `failed · ${stepLabel(r.failedStep)}` : tone === 'warn' ? 'went off' : 'ok'}
        </span>
      </td>
      <td>
        <Link to={`/ai-observability/tracing/${r.observabilityId}`} style={{ color: 'var(--obs-ink)',
          textDecoration: 'none', fontWeight: 500, display: 'block', maxWidth: 320, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.name ?? undefined}>{r.name ?? '—'}</Link>
      </td>
      <td><span className="obs-chip" style={{ color: KIND_COLOR[r.kind], fontWeight: 600 }}>{kindLabel(r.kind)}</span></td>
      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.modelId ?? '—'}</td>
      <td className="num">{fmtMs(r.latencyMs)}</td>
      <td style={{ textAlign: 'center' }}>
        <Link to={`/ai-observability/tracing/${r.observabilityId}`} aria-label="View"
          title="View" data-testid="AiObservability.Action.View" style={{ display: 'inline-flex', padding: 6 }}>
          <i className="pi pi-eye text-blue-600" style={{ fontSize: '1.05rem' }} />
        </Link>
      </td>
    </tr>
  );
}

export default function AiObservability() {
  const [range, setRange] = useState('7d');
  const fetchOverview = useCallback(() => aiObservabilityService.getOverview({ timeRange: range }), [range]);
  const { data, loading, error, reload } = useObsQuery(fetchOverview, 'Could not load the overview.');
  const s = data?.summary;
  const has = !!s && s.calls > 0;

  const kpis = [
    { label: 'AI calls', val: s?.calls.toLocaleString() ?? '—', color: undefined },
    { label: 'Healthy', val: pct(s?.healthyRate ?? null), color: INK.ok,
      note: s ? `${s.ok.toLocaleString()} did what they should${s.failuresOnlyCalls
        ? ` · ${s.failuresOnlyCalls.toLocaleString()} from failures-only types not counted` : ''}` : '' },
    { label: 'Broke', val: s?.failed.toLocaleString() ?? '—', color: s?.failed ? INK.fail : undefined,
      note: 'stopped with an error' },
    { label: 'Went off', val: s?.wentOff.toLocaleString() ?? '—', color: s?.wentOff ? INK.warn : undefined,
      note: 'answered, but something was wrong' },
  ];

  return (
    <div className="ea-root obs" style={{ padding: 20, minHeight: '100%' }}>
      <ObsHeader subtitle="How your AI did — what broke, where it went off, and why."
        range={range} onRange={setRange} onRefresh={reload} loading={loading} />

      <ErrorCard error={error} gap />
      {data && !data.enabled && (
        <div className="obs-banner warn">
          Observability is switched off in the <Link to="/ai-observability/master">Observability Master</Link> —
          nothing new is being recorded, not even failures. What is below is from before.
        </div>
      )}

      <div className="ea-kpis obs-kpis-4" style={{ marginBottom: 16, opacity: loading && data ? 0.55 : 1 }}>
        {kpis.map((k) => (
          <div key={k.label} className="ea-card ea-kpi">
            <div className="lab">{k.label}</div>
            <div className="val num" style={{ color: k.color }}>{k.val}</div>
            {k.note && <div className="ea-sub" style={{ fontSize: 11.5, marginTop: 2 }}>{k.note}</div>}
          </div>
        ))}
      </div>

      {!loading && !error && !has && (
        <section className="ea-card pad">
          <div className="obs-empty">
            {data && !data.enabled
              ? 'Nothing recorded in this window — observability is switched off.'
              : 'No AI calls in this window. Run a prompt or ask a RAG, then widen the range.'}
          </div>
        </section>
      )}

      {data && has && (
        <div className="ea-grid" style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
          <WhereItWentOff data={data} />

          <section className="ea-card pad ea-c8" style={{ minWidth: 0 }}>
            <CardHead icon={ICONS.eval} title="Needs attention"
              sub={data.attentionTotal
                ? `Problems grouped by what ran and what went wrong — AI behaviour first, setup last.${data.attentionTotal > data.attention.length ? ` Top ${data.attention.length} of ${data.attentionTotal}.` : ''} Click one for its latest trace.`
                : undefined} />
            {data.attention.length
              ? <div>{data.attention.map((a) => <AttentionRow key={`${a.name}|${a.problem}`} a={a} />)}</div>
              : <div className="obs-empty">Nothing went wrong in this window.</div>}
          </section>

          <section className="ea-card pad ea-c4" style={{ minWidth: 0 }}>
            <CardHead icon={ICONS.calls} title="By type" />
            {data.byType.map((t) => (
              <div key={t.type} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
                borderTop: '1px solid var(--obs-line-soft)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{OBS_TYPE_INFO[t.type].label}</div>
                  <div className="ea-sub" style={{ fontSize: 11.5 }}>
                    {t.calls.toLocaleString()} calls{t.cost != null ? ` · ${fmtCost(t.cost, data.spend.currency)}` : ''}
                  </div>
                </div>
                {!t.observing && (
                  <Link to="/ai-observability/master" className="obs-chip" style={{ textDecoration: 'none' }}
                    title="Switched off in the Observability Master — only failures are recorded">failures only</Link>
                )}
                {t.failed > 0 && <span className="obs-chip" style={CHIP_FAIL}>{t.failed} broke</span>}
                {t.observing && t.wentOff > 0 && <span className="obs-chip" style={WARN}>{t.wentOff} off</span>}
                {t.observing && !t.failed && !t.wentOff && <span className="obs-chip" style={OK}>all ok</span>}
              </div>
            ))}
          </section>

          <section className="ea-card ea-c12" style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px 0' }}>
              <CardHead icon={ICONS.table} title="Latest calls"
                right={<Link to="/ai-observability/tracing" className="obs-chip" style={{ textDecoration: 'none' }}>All calls →</Link>} />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="obs-tbl">
                <thead>
                  <tr><th>When</th><th>Outcome</th><th>What ran</th><th>Kind</th><th>Model</th><th className="num">Took</th><th style={{ textAlign: 'center' }}>Actions</th></tr>
                </thead>
                <tbody>{data.recent.map((r) => <RecentRow key={r.observabilityId} r={r} />)}</tbody>
              </table>
            </div>
            <ObsState loading={false} error={null} rows={data.recent.length} empty="No calls." />
          </section>

          <section className="ea-card pad ea-c12" style={{ minWidth: 0 }}>
            <CardHead icon={ICONS.cost} title="Cost & speed" />
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 13 }}>
              <span><span className="ea-sub">Spend</span> <b>{fmtCost(data.spend.cost, data.spend.currency)}</b></span>
              <span><span className="ea-sub">Average call</span> <b>{fmtMs(data.spend.avgLatencyMs)}</b></span>
              <span><span className="ea-sub">Slowest 5% over</span> <b>{fmtMs(data.spend.p95LatencyMs)}</b></span>
            </div>
            {(data.spend.costNotCaptured > 0 || data.spend.groundingChecks > 0) && (
              <div className="ea-sub" style={{ fontSize: 12, marginTop: 8 }}>
                {data.spend.groundingChecks > 0
                  && `Includes ${data.spend.groundingChecks} grounding-check judge call${data.spend.groundingChecks === 1 ? '' : 's'}. `}
                {data.spend.costNotCaptured > 0
                  && `Missing the cost of ${data.spend.costNotCaptured} call${data.spend.costNotCaptured === 1 ? '' : 's'} — "Cost & tokens" was off for their type.`}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
